import { ArrowLeft, Loader2, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import type { CharacterCreationDraftDnd5e } from "@/lib/core/schemas/character-creation";
import { commitCharacterCreation } from "@/src/api/character-creation";
import { callMcpTool } from "@/src/api/mcp";

type LoadingStepProps = {
  campaignId: string;
  draft: CharacterCreationDraftDnd5e | null;
  expectedPlanDigest: string | null;
  templateId: string | null;
  onComplete: () => void;
  onBack: () => void;
};

type LoadingState =
  | { phase: "running"; message: string }
  | { phase: "error"; message: string }
  | { phase: "done" };

const phaseError = (phase: string, err: unknown): Error => {
  const message = err instanceof Error ? err.message : String(err);
  return new Error(`${phase} failed: ${message}`);
};

export function LoadingStep({
  campaignId,
  draft,
  expectedPlanDigest,
  templateId,
  onComplete,
  onBack,
}: LoadingStepProps) {
  const [state, setState] = useState<LoadingState>({
    phase: "running",
    message: "Preparing...",
  });
  const running = useRef(false);
  const characterCommitted = useRef(false);
  const scenarioApplied = useRef(false);

  const execute = useCallback(async () => {
    // Guard against React Strict Mode double-firing the effect
    if (running.current) {
      return;
    }
    running.current = true;
    setState({ phase: "running", message: "Preparing..." });

    try {
      if (draft && expectedPlanDigest && !characterCommitted.current) {
        setState({ phase: "running", message: "Creating your character..." });
        try {
          await commitCharacterCreation(campaignId, draft, expectedPlanDigest);
          characterCommitted.current = true;
        } catch (err) {
          throw phaseError("Creating character", err);
        }
      }

      if (templateId && !scenarioApplied.current) {
        setState({ phase: "running", message: "Preparing adventure..." });
        try {
          await callMcpTool(
            "apply_scenario_template",
            { templateId },
            campaignId
          );
          scenarioApplied.current = true;
        } catch (err) {
          throw phaseError("Preparing adventure", err);
        }
      }

      setState({ phase: "done" });
    } catch (err) {
      running.current = false;
      setState({
        phase: "error",
        message: err instanceof Error ? err.message : "Something went wrong.",
      });
    }
  }, [campaignId, draft, expectedPlanDigest, templateId]);

  useEffect(() => {
    execute();
  }, [execute]);

  useEffect(() => {
    if (state.phase === "done") {
      onComplete();
    }
  }, [state.phase, onComplete]);

  if (state.phase === "error") {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-6 px-4">
        <div className="text-center">
          <h2 className="font-semibold text-destructive text-xl">
            Setup failed
          </h2>
          <p className="mt-2 max-w-sm text-muted-foreground text-sm">
            {state.message}
          </p>
          {characterCommitted.current ? (
            <p className="mt-2 max-w-sm text-muted-foreground text-sm">
              Your character was created. Retry will only prepare the adventure.
            </p>
          ) : null}
        </div>
        <div className="flex gap-3">
          <Button onClick={onBack} type="button" variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Go back
          </Button>
          <Button onClick={execute} type="button">
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <p className="text-muted-foreground text-sm">
        {state.phase === "running" ? state.message : ""}
      </p>
    </div>
  );
}
